import { AuthService } from 'phoenix/services';

export interface SlideConfig {
    closeButtonText?:string;
    color?:string;
    goForwardButtonText?:string;
    link?:string;
	text:string;
    title:string;
}

interface SlideValidator {
    ():ng.IPromise<boolean>|boolean;
}

let DefaultSlideConfig:SlideConfig = {
    title:null,
    text:null
}

export abstract class Slide {
    public closeButtonText?:string;
    public color?:string;
    public goForwardButtonText?:string;
    public link?:string;
	public text:string;
    public title:string;
    public visible:boolean = false;
    
    private validators:SlideValidator[] = [];

    constructor(
        private $q:ng.IQService,
        private gettextCatalog:ng.gettext.gettextCatalog,
        {
            closeButtonText,
            color,
            goForwardButtonText,
            link,
            text,
            title
        }:SlideConfig = DefaultSlideConfig
    ) {
        this.title = title;
        this.text = text;
        this.closeButtonText = closeButtonText || this.gettextCatalog.getString('Got It');
        this.color = color || 'primary';
        this.goForwardButtonText = goForwardButtonText || this.gettextCatalog.getString('Next');
        this.link = link;
    }

    public addValidator(validator:SlideValidator) {
        this.validators[this.validators.length] = validator;
    }

    public validate() {
        this.visible = false;
        let p = this.$q.resolve(true);        
        if(this.validators && this.validators.length > 0) {
            p = this.$q.all(this.validators.map(v => v.call(this)))
                .then(results => !results.some(result => result === false));
        }

        return p.then(result => this.visible = result);
    }
}

export class ImageSlide extends Slide {
    constructor(
        $q:ng.IQService,
        gettextCatalog:ng.gettext.gettextCatalog,
        public imageFileName:string,
        slideData:SlideConfig
    ) {
        super($q, gettextCatalog, slideData);
        this.imageFileName = imageFileName;
    }

    public get backgroundImageStyle() {
        return {
            background: `url('images/${this.imageFileName}')`,
            'background-size': '100% 100%'
        }
    }
}

//Created ISlideData to mimic named parameters in the IconSlide constructor
export interface ISlideData {
    slideData:SlideConfig
}

export interface IconSlideConfig extends ISlideData {
    materialIconName?:string,
    svgIconName?:string
}

export class IconSlide extends Slide {
	materialIconName?:string;
    svgIconName?:string;

    constructor(
        $q:ng.IQService,
        gettextCatalog:ng.gettext.gettextCatalog,
        {
            materialIconName,
            svgIconName,
            slideData
        }:IconSlideConfig
    ) {
        if(materialIconName == null && svgIconName == null) {
            throw new Error("An icon slide requires a material icon name or an svg icon name");
        }

        super($q, gettextCatalog, slideData);
        this.materialIconName = materialIconName;
        this.svgIconName = svgIconName;
    }
}

export class RoleRestrictedSlide extends Slide {
    constructor(
        $q:ng.IQService,
        gettextCatalog:ng.gettext.gettextCatalog,
        pxAuthService:AuthService,
        public allowedRoles:string[],
        slideData?:SlideConfig
    ) {
        super($q, gettextCatalog, slideData);
        this.allowedRoles = allowedRoles;
        this.addValidator(() => {
            let passesRoleValidation = false;
            this.allowedRoles.forEach(allowedRole => {
                switch(allowedRole) {
                    case 'Admin':
                        if(pxAuthService.isAdmin) {
                            passesRoleValidation = true;
                        }
                        break;
                    case 'SiteAdmin':
                        if(pxAuthService.isSiteAdmin) {
                            passesRoleValidation = true;
                        }
                        break;
                    case 'UserAdmin':
                        if(pxAuthService.isUserAdmin) {
                            passesRoleValidation = true;
                        }
                        break;
                    case 'TenantAdmin':
                        if(pxAuthService.isTenantAdmin) {
                            passesRoleValidation = true;
                        }
                        break;
                    case 'SysAdmin':
                        if(pxAuthService.isSysAdmin) {
                            passesRoleValidation = true;
                        }
                        break;
                    default:
                        throw new Error("Unknown role used in slide validation");
                }
            });

            return passesRoleValidation;
        });
    }
}

export function isRoleRestrictedSlide(item: Slide): item is RoleRestrictedSlide {
    return Object.prototype.hasOwnProperty.call(item, 'allowedRoles');
}

export type RoleRestrictedIconSlide = RoleRestrictedSlide & IconSlide;
export type RoleRestrictedImageSlide = RoleRestrictedSlide & ImageSlide;


// WEBPACK FOOTER //
// ./src/sharebase-app/domain/slides.ts